import React, { useState } from 'react';
import { NewsAggregationEditorProps, NewsEvent, RerunAggregationParams } from '../types';
import Typography from '../../../basic/Typography';
import newsEditorApi from '../services/newsEditorApi';
import styles from '../NewsEditor.module.css';

const NewsAggregationEditor: React.FC<NewsAggregationEditorProps> = ({
  jobId,
  aggregation,
  onUpdate,
  onRerun,
  queryTopic,
  isNewJob
}) => {
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [isRerunning, setIsRerunning] = useState(false);
  const [showRerunForm, setShowRerunForm] = useState(false);
  const [rerunParams, setRerunParams] = useState<RerunAggregationParams>({
    topic: queryTopic || '',
    location: 'US',
    search_level: 'low',
    language: 'en',
    max_events: 10,
    enable_enrichment: false,
    aggregation_source: 'perplexity',
    timerange: '24 hours',
    search_after_date: new Date(Date.now() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
    search_before_date: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  });

  const [newEvent, setNewEvent] = useState<Omit<NewsEvent, 'id'>>({
    headline: '',
    ongoing_context: '',
    details_spoken: '',
    details_annotate: '',
    summary: '',
    source_annotated_details: '',
    sources: [''],
    timeline: [],
    geo_location: '',
    tags: []
  });

  const [editingEvent, setEditingEvent] = useState<NewsEvent>({
    id: '',
    headline: '',
    ongoing_context: '',
    details_spoken: '',
    details_annotate: '',
    summary: '',
    source_annotated_details: '',
    sources: [''],
    timeline: [],
    geo_location: '',
    tags: []
  });

  // Helper function to safely extract events array from aggregation data
  const getEventsArray = (): NewsEvent[] => {
    console.log('Aggregation data:', aggregation);
    console.log('news_events type:', typeof aggregation.news_events);
    console.log('news_events value:', aggregation.news_events);

    if (!aggregation || !aggregation.news_events) {
      console.log('No aggregation data, using empty array');
      return [];
    }

    if (Array.isArray(aggregation.news_events)) {
      console.log('Using direct array');
      return aggregation.news_events;
    } else if (aggregation.news_events && typeof aggregation.news_events === 'object' && 'events' in aggregation.news_events) {
      console.log('Using events property');
      return aggregation.news_events.events;
    }
    console.log('Using empty array as fallback');
    return [];
  };

  const events = getEventsArray();

  const handleAddEvent = async () => {
    try {
      const addedEvent = await newsEditorApi.addNewsEvent(jobId, newEvent);
      const updatedEvents = [...events, addedEvent];
      onUpdate(updatedEvents);

      // Reset form
      setNewEvent({
        headline: '',
        ongoing_context: '',
        details_spoken: '',
        details_annotate: '',
        summary: '',
        source_annotated_details: '',
        sources: [''],
        timeline: [],
        geo_location: '',
        tags: []
      });
      setIsAddingEvent(false);
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to add news event');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (window.confirm('Are you sure you want to delete this news event?')) {
      try {
        await newsEditorApi.deleteNewsEvent(jobId, eventId);
        const updatedEvents = events.filter(event => event.id !== eventId);
        onUpdate(updatedEvents);
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Failed to delete news event');
      }
    }
  };

  const handleEditEvent = (event: NewsEvent) => {
    setEditingEvent(event);
    setEditingEventId(event.id);
  };

  const handleUpdateEvent = async () => {
    try {
      // Update the event in the local array first
      const updatedEvents = events.map(event =>
        event.id === editingEventId ? editingEvent : event
      );

      // Update on the backend
      await newsEditorApi.updateNewsEvents(jobId, updatedEvents);

      // Update the parent component
      onUpdate(updatedEvents);

      // Reset editing state
      setEditingEventId(null);
      setEditingEvent({
        id: '',
        headline: '',
        ongoing_context: '',
        details_spoken: '',
        details_annotate: '',
        summary: '',
        source_annotated_details: '',
        sources: [''],
        timeline: [],
        geo_location: '',
        tags: []
      });
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update news event');
    }
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setEditingEvent({
      id: '',
      headline: '',
      ongoing_context: '',
      details_spoken: '',
      details_annotate: '',
      summary: '',
      source_annotated_details: '',
      sources: [''],
      timeline: [],
      geo_location: '',
      tags: []
    });
  };

  const handleRerunAggregation = async () => {
    if (showRerunForm) {
      // Submit the form with current parameters
      try {
        setIsRerunning(true);

        // Call the backend aggregation endpoint to overwrite the existing job
        const response = await fetch('/news/api/newsevents/aggregate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pipeline_job: { job_uuid: jobId }, // Use existing job ID to overwrite
            topic: rerunParams.topic,
            location: rerunParams.location,
            search_level: rerunParams.search_level,
            language: rerunParams.language,
            max_events: rerunParams.max_events,
            enable_enrichment: rerunParams.enable_enrichment,
            aggregation_source: rerunParams.aggregation_source,
            timerange: rerunParams.timerange,
            search_after_date: rerunParams.search_after_date,
            search_before_date: rerunParams.search_before_date
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('Aggregation rerun started successfully:', result);

        // Call the parent onRerun to trigger UI updates
        await onRerun(rerunParams);
        setShowRerunForm(false);

      } catch (error) {
        console.error('Error rerunning aggregation:', error);
        alert('Failed to rerun news aggregation');
      } finally {
        setIsRerunning(false);
      }
    } else {
      // Show the form
      setShowRerunForm(true);
    }
  };

  const handleCancelRerun = () => {
    setShowRerunForm(false);
    // Reset to defaults
    setRerunParams({
      topic: queryTopic || '',
      location: 'US',
      search_level: 'low',
      language: 'en',
      max_events: 10,
      enable_enrichment: false,
      aggregation_source: 'perplexity',
      timerange: '24 hours',
      search_after_date: new Date(Date.now() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      search_before_date: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    });
  };



  return (
    <div className={styles['news-aggregation-editor']}>
      <div className={styles['editor-header']}>
        <Typography variant="h4">News Events ({events.length})</Typography>
        <div className={styles['editor-actions']}>
          <button
            className={styles['add-event-button']}
            onClick={() => setIsAddingEvent(!isAddingEvent)}
          >
            {isAddingEvent ? 'Cancel' : 'Add Event'}
          </button>
          <button
            className={styles['rerun-button']}
            onClick={handleRerunAggregation}
            disabled={isRerunning}
            title={isNewJob ? "Start news aggregation for new job" : "Rerun news aggregation to fetch fresh events"}
          >
            {isRerunning ? 'Rerunning...' : (isNewJob ? 'Start Aggregation' : 'Rerun Aggregation')}
          </button>
        </div>
      </div>

      {/* Rerun Aggregation Form */}
      {showRerunForm && (
        <div className={styles['rerun-aggregation-form']}>
          <Typography variant="h5">{isNewJob ? 'Start News Aggregation' : 'Rerun News Aggregation'}</Typography>
          <Typography variant="body2" color="secondary" style={{ marginBottom: '1rem' }}>
            {isNewJob
              ? 'Configure aggregation parameters to start the news aggregation process for your new job.'
              : 'Modify aggregation parameters and rerun the news aggregation process.'
            }
          </Typography>

          <div className={styles['form-grid']}>
            {/* Query Topic */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-topic" className={styles['form-label']}>Query Topic *</label>
              <input
                id="rerun-topic"
                type="text"
                placeholder="Enter the main topic to search for"
                value={rerunParams.topic}
                onChange={(e) => setRerunParams({ ...rerunParams, topic: e.target.value })}
                className={styles['form-input']}
                required
              />
            </div>

            {/* Location */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-location" className={styles['form-label']}>Location (Country Code)</label>
              <input
                id="rerun-location"
                type="text"
                placeholder="US, GB, FR, etc."
                value={rerunParams.location || ''}
                onChange={(e) => setRerunParams({ ...rerunParams, location: e.target.value || undefined })}
                className={styles['form-input']}
              />
              <small className={styles['form-help']}>ISO 3166-1 alpha-2 country code (e.g., US, GB, FR)</small>
            </div>

            {/* Search Level */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-search-level" className={styles['form-label']}>Search Level</label>
              <select
                id="rerun-search-level"
                value={rerunParams.search_level}
                onChange={(e) => setRerunParams({ ...rerunParams, search_level: e.target.value as 'low' | 'medium' | 'high' })}
                className={styles['form-select']}
              >
                <option value="low">Low (Cost-efficient)</option>
                <option value="medium">Medium (Balanced)</option>
                <option value="high">High (Comprehensive)</option>
              </select>
            </div>

            {/* Language */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-language" className={styles['form-label']}>Language</label>
              <select
                id="rerun-language"
                value={rerunParams.language}
                onChange={(e) => setRerunParams({ ...rerunParams, language: e.target.value })}
                className={styles['form-select']}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="it">Italian</option>
                <option value="pt">Portuguese</option>
                <option value="ru">Russian</option>
                <option value="zh">Chinese</option>
                <option value="ja">Japanese</option>
                <option value="ko">Korean</option>
              </select>
            </div>

            {/* Max Events */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-max-events" className={styles['form-label']}>Max Events</label>
              <input
                id="rerun-max-events"
                type="number"
                min="1"
                max="50"
                value={rerunParams.max_events}
                onChange={(e) => setRerunParams({ ...rerunParams, max_events: parseInt(e.target.value) })}
                className={styles['form-input']}
              />
            </div>

            {/* Enable Enrichment */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-enrichment" className={styles['form-label']}>Enable Enrichment</label>
              <select
                id="rerun-enrichment"
                value={rerunParams.enable_enrichment.toString()}
                onChange={(e) => setRerunParams({ ...rerunParams, enable_enrichment: e.target.value === 'true' })}
                className={styles['form-select']}
              >
                <option value="false">False</option>
                <option value="true">True</option>
              </select>
            </div>

            {/* Aggregation Source */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-source" className={styles['form-label']}>Aggregation Source</label>
              <select
                id="rerun-source"
                value={rerunParams.aggregation_source}
                onChange={(e) => setRerunParams({ ...rerunParams, aggregation_source: e.target.value as 'perplexity' | 'xai' | 'openrouter' })}
                className={styles['form-select']}
              >
                <option value="perplexity">Perplexity (Sonar)</option>
                <option value="xai">xAI (Grok-4)</option>
                <option value="openrouter">OpenRouter API</option>
              </select>
            </div>

            {/* Timerange */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-timerange" className={styles['form-label']}>Time Range</label>
              <input
                id="rerun-timerange"
                type="text"
                placeholder="24 hours, 7 days, 1 week"
                value={rerunParams.timerange}
                onChange={(e) => setRerunParams({ ...rerunParams, timerange: e.target.value })}
                className={styles['form-input']}
              />
              <small className={styles['form-help']}>Format: &lt;time&gt; &lt;unit&gt; (e.g., 24 hours, 7 days)</small>
            </div>

            {/* Search After Date */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-search-after" className={styles['form-label']}>Search After Date</label>
              <input
                id="rerun-search-after"
                type="text"
                placeholder="MM/DD/YYYY"
                value={rerunParams.search_after_date}
                onChange={(e) => setRerunParams({ ...rerunParams, search_after_date: e.target.value })}
                className={styles['form-input']}
              />
              <small className={styles['form-help']}>Format: MM/DD/YYYY (e.g., 03/01/2025)</small>
            </div>

            {/* Search Before Date */}
            <div className={styles['form-group']}>
              <label htmlFor="rerun-search-before" className={styles['form-label']}>Search Before Date</label>
              <input
                id="rerun-search-before"
                type="text"
                placeholder="MM/DD/YYYY"
                value={rerunParams.search_before_date}
                onChange={(e) => setRerunParams({ ...rerunParams, search_before_date: e.target.value })}
                className={styles['form-input']}
              />
              <small className={styles['form-help']}>Format: MM/DD/YYYY (e.g., 03/05/2025)</small>
            </div>
          </div>

          <div className={styles['form-actions']}>
            <button
              className={styles['save-event-button']}
              onClick={handleRerunAggregation}
              disabled={isRerunning || !rerunParams.topic}
            >
              {isRerunning ? 'Rerunning...' : (isNewJob ? 'Start Aggregation' : 'Rerun Aggregation')}
            </button>
            <button
              className={styles['cancel-button']}
              onClick={handleCancelRerun}
              disabled={isRerunning}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add Event Form */}
      {isAddingEvent && (
        <div className={styles['add-event-form']}>
          <Typography variant="h5">Add New News Event</Typography>

          {/* Basic Information */}
          <div className={styles['form-group']}>
            <label htmlFor="headline" className={styles['form-label']}>Headline *</label>
            <input
              id="headline"
              type="text"
              placeholder="Enter the main headline for this news event"
              value={newEvent.headline}
              onChange={(e) => setNewEvent({ ...newEvent, headline: e.target.value })}
              className={styles['form-input']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="ongoing-context" className={styles['form-label']}>Ongoing Context *</label>
            <input
              id="ongoing-context"
              type="text"
              placeholder="Describe the context in which this news applies"
              value={newEvent.ongoing_context}
              onChange={(e) => setNewEvent({ ...newEvent, ongoing_context: e.target.value })}
              className={styles['form-input']}
            />
          </div>

          {/* Content Fields */}
          <div className={styles['form-group']}>
            <label htmlFor="summary" className={styles['form-label']}>Summary *</label>
            <textarea
              id="summary"
              placeholder="Provide a concise summary of the event"
              value={newEvent.summary}
              onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="details-spoken" className={styles['form-label']}>Details for Spoken Presentation *</label>
            <textarea
              id="details-spoken"
              placeholder="Format details specifically for spoken presentation"
              value={newEvent.details_spoken}
              onChange={(e) => setNewEvent({ ...newEvent, details_spoken: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="details-annotate" className={styles['form-label']}>Details with Annotations *</label>
            <textarea
              id="details-annotate"
              placeholder="Include research paper style annotations"
              value={newEvent.details_annotate}
              onChange={(e) => setNewEvent({ ...newEvent, details_annotate: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="source-annotated" className={styles['form-label']}>Source Annotated Details *</label>
            <textarea
              id="source-annotated"
              placeholder="Detailed content with source annotations"
              value={newEvent.source_annotated_details}
              onChange={(e) => setNewEvent({ ...newEvent, source_annotated_details: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>

          {/* Sources and Metadata */}
          <div className={styles['form-group']}>
            <label htmlFor="sources" className={styles['form-label']}>Sources *</label>
            <input
              id="sources"
              type="text"
              placeholder="Enter source URLs separated by commas"
              value={newEvent.sources.join(', ')}
              onChange={(e) => setNewEvent({
                ...newEvent,
                sources: e.target.value.split(',').map(s => s.trim()).filter(s => s)
              })}
              className={styles['form-input']}
            />
            <small className={styles['form-help']}>Separate multiple sources with commas</small>
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="geo-location" className={styles['form-label']}>Geographic Location</label>
            <input
              id="geo-location"
              type="text"
              placeholder="Enter geographic location if applicable"
              value={newEvent.geo_location || ''}
              onChange={(e) => setNewEvent({ ...newEvent, geo_location: e.target.value })}
              className={styles['form-input']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="tags" className={styles['form-label']}>Tags</label>
            <input
              id="tags"
              type="text"
              placeholder="Enter relevant tags separated by commas"
              value={newEvent.tags.join(', ')}
              onChange={(e) => setNewEvent({
                ...newEvent,
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
              })}
              className={styles['form-input']}
            />
            <small className={styles['form-help']}>Separate multiple tags with commas</small>
          </div>

          <div className={styles['form-actions']}>
            <button
              className={styles['save-event-button']}
              onClick={handleAddEvent}
              disabled={!newEvent.headline || !newEvent.summary || !newEvent.ongoing_context ||
                       !newEvent.details_spoken || !newEvent.details_annotate ||
                       !newEvent.source_annotated_details || newEvent.sources.length === 0}
            >
              Add Event
            </button>
          </div>
        </div>
      )}

            {/* Edit Event Form */}
      {editingEventId && (
        <div className={styles['edit-event-form']}>
          <Typography variant="h5">Edit News Event</Typography>

          {/* Basic Information */}
          <div className={styles['form-group']}>
            <label htmlFor="edit-headline" className={styles['form-label']}>Headline *</label>
            <input
              id="edit-headline"
              type="text"
              placeholder="Enter the main headline for this news event"
              value={editingEvent.headline}
              onChange={(e) => setEditingEvent({ ...editingEvent, headline: e.target.value })}
              className={styles['form-input']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="edit-ongoing-context" className={styles['form-label']}>Ongoing Context *</label>
            <input
              id="edit-ongoing-context"
              type="text"
              placeholder="Describe the context in which this news applies"
              value={editingEvent.ongoing_context}
              onChange={(e) => setEditingEvent({ ...editingEvent, ongoing_context: e.target.value })}
              className={styles['form-input']}
            />
          </div>

          {/* Content Fields */}
          <div className={styles['form-group']}>
            <label htmlFor="edit-summary" className={styles['form-label']}>Summary *</label>
            <textarea
              id="edit-summary"
              placeholder="Provide a concise summary of the event"
              value={editingEvent.summary}
              onChange={(e) => setEditingEvent({ ...editingEvent, summary: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="edit-details-spoken" className={styles['form-label']}>Details for Spoken Presentation *</label>
            <textarea
              id="edit-details-spoken"
              placeholder="Format details specifically for spoken presentation"
              value={editingEvent.details_spoken}
              onChange={(e) => setEditingEvent({ ...editingEvent, details_spoken: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="edit-details-annotate" className={styles['form-label']}>Details with Annotations *</label>
            <textarea
              id="edit-details-annotate"
              placeholder="Include research paper style annotations"
              value={editingEvent.details_annotate}
              onChange={(e) => setEditingEvent({ ...editingEvent, details_annotate: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="edit-source-annotated" className={styles['form-label']}>Source Annotated Details *</label>
            <textarea
              id="edit-source-annotated"
              placeholder="Detailed content with source annotations"
              value={editingEvent.source_annotated_details}
              onChange={(e) => setEditingEvent({ ...editingEvent, source_annotated_details: e.target.value })}
              className={styles['form-textarea']}
            />
          </div>

          {/* Sources and Metadata */}
          <div className={styles['form-group']}>
            <label htmlFor="edit-sources" className={styles['form-label']}>Sources *</label>
            <input
              id="edit-sources"
              type="text"
              placeholder="Enter source URLs separated by commas"
              value={editingEvent.sources.join(', ')}
              onChange={(e) => setEditingEvent({
                ...editingEvent,
                sources: e.target.value.split(',').map(s => s.trim()).filter(s => s)
              })}
              className={styles['form-input']}
            />
            <small className={styles['form-help']}>Separate multiple sources with commas</small>
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="edit-geo-location" className={styles['form-label']}>Geographic Location</label>
            <input
              id="edit-geo-location"
              type="text"
              placeholder="Enter geographic location if applicable"
              value={editingEvent.geo_location || ''}
              onChange={(e) => setEditingEvent({ ...editingEvent, geo_location: e.target.value })}
              className={styles['form-input']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="edit-tags" className={styles['form-label']}>Tags</label>
            <input
              id="edit-tags"
              type="text"
              placeholder="Enter relevant tags separated by commas"
              value={editingEvent.tags.join(', ')}
              className={styles['form-input']}
              onChange={(e) => setEditingEvent({
                ...editingEvent,
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
              })}
            />
            <small className={styles['form-help']}>Separate multiple tags with commas</small>
          </div>

          <div className={styles['form-actions']}>
            <button
              className={styles['save-event-button']}
              onClick={handleUpdateEvent}
              disabled={!editingEvent.headline || !editingEvent.summary || !editingEvent.ongoing_context ||
                       !editingEvent.details_spoken || !editingEvent.details_annotate ||
                       !editingEvent.source_annotated_details || editingEvent.sources.length === 0}
            >
              Update Event
            </button>
            <button
              className={styles['cancel-button']}
              onClick={handleCancelEdit}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className={styles['events-list']}>
        {events.map((event) => (
          <div key={event.id} className={styles['news-event-item']}>
            <div className={styles['event-header']}>
              <Typography variant="h5">{event.headline}</Typography>
              <div className={styles['event-tags']}>
                {event.tags.slice(0, 3).map((tag, index) => (
                  <span key={index} className={styles['event-tag']}>{tag}</span>
                ))}
                {event.tags.length > 3 && (
                  <span className={styles['event-tag-more']}>+{event.tags.length - 3}</span>
                )}
              </div>
            </div>

            <Typography variant="body2" className={styles['event-summary']}>{event.summary}</Typography>

            <div className={styles['event-details']}>
              <Typography variant="body2" className={styles['event-context']}>
                <strong>Context:</strong> {event.ongoing_context}
              </Typography>
              <Typography variant="body2" className={styles['event-details-spoken']}>
                <strong>Spoken Details:</strong> {event.details_spoken}
              </Typography>
            </div>

            <div className={styles['event-meta']}>
              <div className={styles['event-sources']}>
                <Typography variant="caption" color="tertiary">
                  <strong>Sources:</strong> {event.sources.slice(0, 2).join(', ')}
                  {event.sources.length > 2 && ` +${event.sources.length - 2} more`}
                </Typography>
              </div>

              {event.geo_location && (
                <Typography variant="caption" color="tertiary" className={styles['event-location']}>
                  📍 {event.geo_location}
                </Typography>
              )}

              {event.timeline.length > 0 && (
                <Typography variant="caption" color="tertiary" className={styles['event-timeline']}>
                  📅 {event.timeline.length} timeline events
                </Typography>
              )}

              <div className={styles['event-actions']}>
                <button
                  className={styles['edit-event-button']}
                  onClick={() => handleEditEvent(event)}
                  title="Edit this news event"
                  disabled={editingEventId !== null}
                >
                  ✏️
                </button>
                <button
                  className={styles['delete-event-button']}
                  onClick={() => handleDeleteEvent(event.id)}
                  title="Delete this news event"
                  disabled={editingEventId !== null}
                >
                  🗑️
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles['editor-footer']}>
        <Typography variant="caption" color="secondary">Search Query: {aggregation.metadata?.search_query || 'N/A'}</Typography>
        <Typography variant="caption" color="secondary">Search Level: {aggregation.metadata?.search_level || 'N/A'}</Typography>
        <Typography variant="caption" color="secondary">Last Updated: {aggregation.metadata?.last_updated ? new Date(aggregation.metadata.last_updated).toLocaleString() : 'N/A'}</Typography>
      </div>
    </div>
  );
};

export default NewsAggregationEditor;
